import { useMemo } from "react";
import { Checkbox } from "@/components/ui/checkbox";

export type SelectableMember = {
  id: string;
  label: string;
};

type MembersSelectListProps = {
  members: SelectableMember[];
  selectedMemberIds: string[];
  onSelectedMemberIdsChange: (selectedMemberIds: string[]) => void;
  title?: string;
  emptyMessage?: string;
  disabled?: boolean;
  className?: string;
};

export function MembersSelectList({
  members,
  selectedMemberIds: selectedMembers,
  onSelectedMemberIdsChange: onSelectedMembersChange,
  title,
  emptyMessage = "No members available.",
  disabled = false,
  className,
}: MembersSelectListProps) {
  const memberIds = useMemo(() => members.map((member) => member.id), [members]);
  const memberIdSet = useMemo(() => new Set(memberIds), [memberIds]);

  const selectedInListCount = selectedMembers.filter((id) => memberIdSet.has(id)).length;

  const allSelected = memberIds.length > 0 && selectedInListCount === memberIds.length;
  const someSelected = selectedInListCount > 0 && selectedInListCount < memberIds.length;

  const selectAllChecked: boolean | "indeterminate" = allSelected
    ? true
    : someSelected
      ? "indeterminate"
      : false;

  const toggleAllMembers = (nextChecked: boolean) => {
    const selectedOutsideList = selectedMembers.filter((id) => !memberIdSet.has(id));

    onSelectedMembersChange(
      nextChecked ? [...selectedOutsideList, ...memberIds] : selectedOutsideList,
    );
  };

  const toggleMember = (memberId: string, nextChecked: boolean) => {
    if (!memberIdSet.has(memberId)) return;

    if (nextChecked) {
      if (selectedMembers.includes(memberId)) return;
      onSelectedMembersChange([...selectedMembers, memberId]);
      return;
    }

    onSelectedMembersChange(selectedMembers.filter((id) => id !== memberId));
  };

  return (
    <div className={className || "space-y-2"}>
      {!!title && <h3 className="text-sm font-medium">{title}</h3>}

      <div className="max-h-64 space-y-2 overflow-y-auto rounded-md border p-2">
        {members.length === 0 ? (
          <p className="text-muted-foreground text-sm">{emptyMessage}</p>
        ) : (
          <>
            <label className="flex cursor-pointer items-center gap-2 border-b pb-2 text-sm font-medium">
              <Checkbox
                checked={selectAllChecked}
                onCheckedChange={(checked) => toggleAllMembers(checked === true)}
                disabled={disabled}
              />
              <span>Select all</span>
            </label>

            {members.map((member) => (
              <label key={member.id} className="flex cursor-pointer items-center gap-2 text-sm">
                <Checkbox
                  checked={selectedMembers.includes(member.id)}
                  onCheckedChange={(checked) => toggleMember(member.id, checked === true)}
                  disabled={disabled}
                />
                <span className="truncate">{member.label}</span>
              </label>
            ))}
          </>
        )}
      </div>
    </div>
  );
}
